import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { z } from 'zod';
import { useIsDialogOpened } from '../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import { formatToPascalCase } from '../../../../../react-commons/utils/utilFunctions';
import { useEnabledQuery } from '../../../ui/hooks/useEnabledQuery';
import { setToDirty } from '../../../ui/ui-state/local-status/localStatusActions';
import { showNotification } from '../../../ui/util/showNotification';
import { ResourceType } from '../../enumerations/ResourceType';
import { ValidationType } from '../../enumerations/ValidationType';
import {
    DetailedValidationResource,
    DetailedValidationResourceSchema,
    transformToDetailedValidationResourceSchema,
    ValidationResource,
    ValidationResourceSchema,
} from '../../resources/validation/detailedValidationResource';
import {
    MutationErrorResponseSchema,
    MutationSuccessResponseOfBatchAddSchema,
    MutationSuccessResponseSchema,
} from '../../resources/validation/errorResponseResource';
import {
    MutationValidationResource,
    MutationValidationResourceSchema,
} from '../../resources/validation/mutationValidationResource';
import { PersistableCreationResultSetSchema } from '../../resources/validation/persistableCreationResultSet';
import { ValidationResultSetSchema } from '../../services/backgroundTaskResource';
import { get, patch, post, remove } from '../APIVersion1';
import {
    DeleteMutateObject,
    DeleteMutateObjectSchema,
    UpdateMutateObject,
    UpdateMutateObjectSchema,
} from '../mutateObjects';
import { PersistableResource, PersistableSchemaType } from '../persistableResourceTypes';
import { ParamConstants, ValidationScopes, ValidationScopeType } from '../types';
import { useMutationWithDelay } from '../useMutationWithDelay';
import { evaluateGetAllResult, useOnUnauthorized, parseRawData } from './AdapterUtils';

export function useResourceAdapterActions<R extends PersistableResource>(
    apiPostfix: string,
    resourceType: ResourceType,
    resourceSchema: PersistableSchemaType<R>
) {
    const dispatch = useDispatch();
    const isDialogOpened = useIsDialogOpened();
    const getEnabled = useEnabledQuery();
    const queryClient = useQueryClient();
    const onUnauthorized = useOnUnauthorized();

    const useGetResource = (id: string | undefined | null) => {
        return useQuery({
            queryKey: [resourceType, id],
            queryFn: () => get(`${apiPostfix}/${id}`).then((result) => processGetResourceResponse(result)),
            enabled: getEnabled(id),
        });
    };

    const useValidateResource = (id: string | undefined | null, validationScopes?: ValidationScopes) => {
        const filteredValidationScopes =
            validationScopes ?
                {
                    validationScopes: validationScopes?.validationScopes.filter((value) =>
                        Boolean(value.projectLineVersion)
                    ),
                }
            :   undefined;
        const jsonScopes = JSON.stringify(
            filteredValidationScopes?.validationScopes?.length ?
                filteredValidationScopes
            :   { validationScopes: [{ type: ValidationScopeType.NO_SCOPE }] }
        );
        return useQuery({
            queryKey: ['VALIDATE', resourceType, id, jsonScopes],
            queryFn: () =>
                get(
                    `${apiPostfix}/${id}/validate?${ParamConstants.SKIP_GLOBAL_VALIDATION_ON_LOCAL_ERROR}=true&${
                        ParamConstants.VALIDATION_SCOPES
                    }=${encodeURIComponent(jsonScopes)}`
                )
                    .then((result) => processGetResponse(result, null, ValidationResourceSchema))
                    .then((validationResource) => {
                        if (validationResource) {
                            return parseRawData(
                                transformToDetailedValidationResourceSchema({ ...validationResource, resourceType }),
                                DetailedValidationResourceSchema
                            );
                        } else {
                            throw Error('The loaded validation resource could not be parsed!');
                        }
                    }),
            enabled: getEnabled(id),
        });
    };

    const useGetDependencyValidation = (id: string | undefined) => {
        return useQuery({
            queryKey: ['DEPENDENCY_VALIDATION', resourceType, id],
            queryFn: () =>
                get(`${apiPostfix}/${id}/dependency-validation`).then((result) =>
                    processGetResponse(result, null, ValidationResultSetSchema)
                ),
            enabled: getEnabled(id),
        });
    };

    const useValidateAllResourcesMultiParented = (
        parentRelation: string,
        parentId: string,
        validationScopes?: ValidationScopes
    ) => {
        const filteredValidationScopes =
            validationScopes ?
                {
                    validationScopes: validationScopes?.validationScopes.filter((value) =>
                        Boolean(value.projectLineVersion)
                    ),
                }
            :   undefined;
        const jsonScopes = JSON.stringify(
            filteredValidationScopes?.validationScopes?.length ?
                filteredValidationScopes
            :   { validationScopes: [{ type: ValidationScopeType.NO_SCOPE }] }
        );
        return useQuery({
            queryKey: ['VALIDATE_ALL', resourceType, parentId, parentRelation, jsonScopes],
            queryFn: () => validateAllResources(parentId, jsonScopes, parentRelation),
            enabled: getEnabled(parentId),
        });
    };

    const useValidateAllResourcesSingleParented = (parentId: string, validationScopes?: ValidationScopes) => {
        const filteredValidationScopes =
            validationScopes ?
                {
                    validationScopes: validationScopes?.validationScopes.filter((value) =>
                        Boolean(value.projectLineVersion)
                    ),
                }
            :   undefined;
        const jsonScopes = JSON.stringify(
            filteredValidationScopes?.validationScopes?.length ?
                filteredValidationScopes
            :   { validationScopes: [{ type: ValidationScopeType.NO_SCOPE }] }
        );
        return useQuery({
            queryKey: ['VALIDATE_ALL', resourceType, parentId, jsonScopes],
            queryFn: () => validateAllResources(parentId as string, jsonScopes),
            enabled: getEnabled(parentId),
        });
    };

    const useUpdateResource = () => {
        return useMutationWithDelay(
            ({ id, resource, validationDepth }: UpdateMutateObject<R>) => {
                const validationDepthParam =
                    validationDepth ?
                        `?${ParamConstants.UPDATE_VALIDATION_DEPTH_REQUEST_PARAM_NAME}=${validationDepth}`
                    :   '';
                return patch(`${apiPostfix}/${id}?${validationDepthParam}`, JSON.stringify(resource)).then((result) =>
                    processMutationResponse(result, 'UPDATE')
                );
            },
            UpdateMutateObjectSchema,
            {
                onSettled: (res) => {
                    if (res?.changed && !isDialogOpened) {
                        dispatch(setToDirty(false));
                    }
                },
            }
        );
    };

    const useDeleteResource = () => {
        return useMutationWithDelay(
            ({ id }: DeleteMutateObject) =>
                remove(
                    `${apiPostfix}/${id}?${ParamConstants.DELETION_STOP_ON_ERROR}=false&${ParamConstants.DELETION_ERROR_LEVEL}=WARNING`
                ).then((result) => processMutationResponse(result, 'DELETE')),
            DeleteMutateObjectSchema,
            undefined,
            (data) => {
                queryClient.setQueryData(['lastDeletionMutation'], Date.now());
                setTimeout(() => {
                    queryClient
                        .invalidateQueries({
                            predicate: (query) =>
                                query.queryKey[0] !== 'INFINITY_STALE_TIME' && !query.queryKey.includes(data.id),
                            type: 'all',
                            refetchType: 'active',
                        })
                        .then();
                }, 100);
            }
        );
    };

    const validateAllResources = (
        parentId: string,
        validationScopesJson: string,
        parentRelation?: string,
        skipGlobalValidation: boolean = true
    ): Promise<Record<string, DetailedValidationResource>> => {
        return get(
            `${apiPostfix}/validate?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${parentId}${getParentRelationText(parentRelation)}&${
                ParamConstants.SKIP_GLOBAL_VALIDATION_ON_LOCAL_ERROR
            }=${skipGlobalValidation}&${ParamConstants.VALIDATION_SCOPES}=${encodeURIComponent(validationScopesJson)}&${
                ParamConstants.DO_NOT_RETURN_EMPTY_RESULTS
            }=true`
        )
            .then((result) => processGetResponse(result, null, z.record(z.string(), ValidationResourceSchema)))
            .then((validationResources) => {
                if (validationResources) {
                    const result: Record<string, ValidationResource & { resourceType: ResourceType }> = {};

                    Object.entries(validationResources).forEach(([key, validationResource]) => {
                        result[key] = transformToDetailedValidationResourceSchema({
                            ...validationResource,
                            resourceType,
                        });
                    });

                    return parseRawData(result, z.record(z.string(), DetailedValidationResourceSchema));
                } else {
                    throw Error('The loaded validation resource could not be parsed!');
                }
            });
    };

    // protected functions used by other adapters

    const getParentRelationText = (parentRelation: string | undefined, prefix: string = '&') =>
        parentRelation ? `${prefix}${ParamConstants.PARENT_RELATION_REQUEST_PARAM_NAME}=${parentRelation}` : '';

    const addOrSetResource = (
        resource: R,
        parentId: string,
        parentRelation?: string,
        withoutGrowl?: boolean
    ): Promise<MutationValidationResource | null> => {
        return post(
            `${apiPostfix}?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${parentId}${getParentRelationText(parentRelation)}`,
            JSON.stringify(resource)
        ).then((result) => processMutationResponse(result, 'ADD', withoutGrowl));
    };

    const batchAddResource = (payload: Record<string, R[]>): Promise<MutationValidationResource | null> => {
        return post(`${apiPostfix}/batch`, JSON.stringify(payload)).then((result) =>
            processMutationResponse(result, 'BATCH_ADD')
        );
    };

    const batchAddResourceWithParentRelation = (
        payload: Record<string, R[]>,
        parentRelation?: string
    ): Promise<MutationValidationResource | null> => {
        return post(`${apiPostfix}/batch${getParentRelationText(parentRelation, '?')}`, JSON.stringify(payload)).then(
            (result) => processMutationResponse(result, 'BATCH_ADD')
        );
    };

    const addResourceToIndex = (
        resource: R,
        index: number | null | undefined,
        parentId: string,
        parentRelation?: string
    ) => {
        const indexArg = index ? `&${ParamConstants.INDEX_REQUEST_PARAM_NAME}=${index}` : '';

        return post(
            `${apiPostfix}?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${parentId}${indexArg}${getParentRelationText(parentRelation)}`,
            JSON.stringify(resource)
        ).then((result) => processMutationResponse(result, 'ADD'));
    };

    const getSingleFromParent = (parentId: string, parentRelation?: string) => {
        return get(
            `${apiPostfix}?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${parentId}${getParentRelationText(parentRelation)}`
        ).then((result) => processGetResourceResponse(result));
    };

    const getAllResourcesWithParent = (parentId: string, parentRelation?: string): Promise<R[] | null> => {
        return get(
            `${apiPostfix}?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${parentId}${getParentRelationText(parentRelation)}&${
                ParamConstants.PER_PAGE_REQUEST_PARAM_NAME
            }=400`
        ).then((result) => processGetCollectionResponse<R>(result));
    };

    const changeIndexOfResource = (resource: R, index: number, parentId: string | undefined) => {
        return patch(
            `${apiPostfix}/${resource?.id}/index/${index}?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${parentId}`
        ).then((result) => processMutationResponse(result, 'INDEX_CHANGE'));
    };

    const getAllResources = (): Promise<R[] | null> => {
        return get(`${apiPostfix}`).then((result) => processGetCollectionResponse<R>(result));
    };

    const processGetResponse = <TYPE, INVALID_TYPE>(
        result: Response,
        ifFailed: INVALID_TYPE,
        schema: z.ZodType<TYPE>
    ): Promise<TYPE | INVALID_TYPE> => {
        if (result.status === 200) {
            return result
                .json()
                .then((rawData) => parseRawData(rawData, schema))
                .catch((e) => {
                    console.error(e);
                    return ifFailed;
                });
        } else if (result.status === 401) {
            return onUnauthorized(result, ifFailed);
        } else {
            result
                .json()
                .then((rawData) => {
                    const parsed = MutationErrorResponseSchema.safeParse(rawData);
                    if (parsed.success) {
                        return parsed.data;
                    }
                    return null;
                })
                .then((value) => {
                    const lastDeletionMutation = queryClient.getQueryData(['lastDeletionMutation']);
                    if (!lastDeletionMutation || Date.now() - (lastDeletionMutation as number) > 2000) {
                        showNotification({
                            severity: 'error',
                            title: 'Could not finish GET request',
                            errorResponseResult: value ?? undefined,
                            validationResourceType: resourceType,
                        });
                    }
                });
        }
        return Promise.reject(`${formatToPascalCase(resourceType)} could not be found`);
    };

    const processGetResourceResponse = (result: Response): Promise<R | null> => {
        if (result.status === 200) {
            return result
                .json()
                .then((resource) => parseRawData(resource, resourceSchema))
                .catch((_error) => {
                    return null;
                    /* TODO Zod
                    if (error instanceof SyntaxError) {
                        return null;
                    } else {
                        throw error;
                    }*/
                });
        } else if (result.status === 401) {
            return onUnauthorized(result, null);
        } else {
            result
                .json()
                .then((rawData) => {
                    const parsed = MutationErrorResponseSchema.safeParse(rawData);
                    if (parsed.success) {
                        return parsed.data;
                    }
                    return null;
                })
                .then((value) => {
                    const lastDeletionMutation = queryClient.getQueryData(['lastDeletionMutation']);
                    if (!lastDeletionMutation || Date.now() - (lastDeletionMutation as number) > 2000) {
                        showNotification({
                            severity: 'error',
                            title: 'Could not finish GET request',
                            errorResponseResult: value ?? undefined,
                            validationResourceType: resourceType,
                        });
                    }
                });
        }
        return Promise.reject(`${formatToPascalCase(resourceType)} could not be found`);
    };

    const processGetCollectionResponse = <TYPE extends object>(result: Response): Promise<Array<TYPE> | null> => {
        if (result.status === 401) {
            return onUnauthorized(result, null);
        }
        return evaluateGetAllResult<TYPE>(result, resourceSchema as unknown as z.Schema<TYPE>);
    };

    // TODO move batchAdd into separate function
    const processMutationResponse = async (
        result: Response,
        mutationType: 'ADD' | 'UPDATE' | 'DELETE' | 'BATCH_ADD' | 'INDEX_CHANGE',
        withoutGrowl?: boolean,
        withoutErrorGrowl?: boolean
    ): Promise<MutationValidationResource | null> => {
        if (result.status === 200 || result.status === 201) {
            if (mutationType === 'BATCH_ADD') {
                return result
                    .json()
                    .then((rawData) => parseRawData(rawData, MutationSuccessResponseOfBatchAddSchema))
                    .then((errorResponseResource) =>
                        parseRawData(errorResponseResource.content, PersistableCreationResultSetSchema)
                    )
                    .then((persistableCreationResultSet) => {
                        if (persistableCreationResultSet.successful) {
                            !withoutGrowl &&
                                showNotification({
                                    severity: 'success',
                                    title: `Successfully added all resources`,
                                    persistableCreationResultSet,
                                    validationResourceType: resourceType,
                                });
                            return {
                                validationType: ValidationType.CREATION,
                                successful: true,
                                changed: true,
                                maxSeverity: null,
                                fatalThreshold: null,
                                content: null,
                                changedProperties: null,
                                nonUpdatablePropertiesChanged: null,
                                validationResult: null,
                                sideEffectResult: null,
                                dependencyValidationResult: null,
                                referencedVersionsNotClosed: null,
                            };
                        } else {
                            !withoutErrorGrowl &&
                                showNotification({
                                    severity: 'error',
                                    title: `Could not add resources`,
                                    persistableCreationResultSet,
                                    validationResourceType: resourceType,
                                });
                            throw Error(`Could not add resources`);
                        }
                    });
            } else if (mutationType === 'INDEX_CHANGE') {
                return result
                    .json()
                    .then((rawData) => parseRawData(rawData, MutationSuccessResponseSchema))
                    .then((mutationValidationResource) => {
                        if (mutationValidationResource.successful && mutationValidationResource.changed) {
                            if (resourceType !== ResourceType.SYSTEM_ANNOUNCEMENT_USER_DISMISSAL) {
                                !withoutGrowl &&
                                    showNotification({
                                        severity: 'success',
                                        title: `Successfully updated index`,
                                        validationResourceType: resourceType,
                                    });
                            }
                        } else if (!mutationValidationResource.successful) {
                            !withoutErrorGrowl &&
                                showNotification({
                                    severity: 'error',
                                    title: `Could not update index`,
                                    validationResourceType: resourceType,
                                });
                        }
                        return null;
                    });
            } else {
                return result
                    .json()
                    .then((rawData) => parseRawData(rawData, MutationSuccessResponseSchema))
                    .then((errorResponseResource) =>
                        parseRawData(errorResponseResource.content, MutationValidationResourceSchema)
                    )
                    .then((mutationValidationResource) => {
                        if (
                            mutationValidationResource.successful &&
                            (mutationType !== 'UPDATE' ||
                                (mutationType === 'UPDATE' &&
                                    (mutationValidationResource.changed ||
                                        Boolean(mutationValidationResource.sideEffectResult))))
                        ) {
                            if (resourceType !== ResourceType.SYSTEM_ANNOUNCEMENT_USER_DISMISSAL) {
                                !withoutGrowl &&
                                    showNotification({
                                        severity: 'success',
                                        title: `Successfully ${
                                            mutationType === 'ADD' ? 'created'
                                            : mutationType === 'UPDATE' ? 'updated'
                                            : 'deleted'
                                        } resource`,
                                        mutationValidationResult: mutationValidationResource,
                                        validationResourceType: resourceType,
                                    });
                            }
                        } else if (
                            mutationValidationResource.successful &&
                            mutationType === 'UPDATE' &&
                            !mutationValidationResource.changed &&
                            !mutationValidationResource.sideEffectResult
                        ) {
                            !withoutErrorGrowl &&
                                showNotification({
                                    severity: 'warning',
                                    title: 'No change happened',
                                    mutationValidationResult: null,
                                    validationResourceType: resourceType,
                                });
                        } else if (!mutationValidationResource.successful) {
                            !withoutErrorGrowl &&
                                showNotification({
                                    severity: 'error',
                                    title: `Could not ${
                                        mutationType === 'ADD' ? 'create'
                                        : mutationType === 'UPDATE' ? 'update'
                                        : 'delete'
                                    } resource`,
                                    mutationValidationResult: mutationValidationResource,
                                    validationResourceType: resourceType,
                                });
                        }
                        return mutationValidationResource;
                    });
            }
        } else if (result.status === 204) {
            !withoutGrowl &&
                showNotification({
                    severity: 'info',
                    title: 'No changes to update resource',
                    validationResourceType: resourceType,
                });
        } else if (result.status === 401) {
            onUnauthorized(result, null).then();
        } else if (result.status === 404) {
            throw Error('Resource could not be found!');
        } else {
            result
                .json()
                .then((rawData) => {
                    const parsed = MutationErrorResponseSchema.safeParse(rawData);
                    if (parsed.success) {
                        return parsed.data;
                    } else {
                        if (mutationType === 'BATCH_ADD') {
                            return parseRawData(rawData, MutationSuccessResponseOfBatchAddSchema);
                        } else {
                            return parseRawData(rawData, MutationSuccessResponseSchema);
                        }
                    }
                })
                .then((value) => {
                    !withoutErrorGrowl &&
                        showNotification({
                            severity: 'error',
                            title: `Could not ${
                                mutationType === 'ADD' || mutationType === 'BATCH_ADD' ? 'create'
                                : mutationType === 'UPDATE' ? 'update'
                                : 'delete'
                            } resource(s)`,
                            errorResponseResult: value,
                            validationResourceType: resourceType,
                        });
                });
        }
        return null;
    };

    return {
        useGetResource,
        useUpdateResource,
        useDeleteResource,
        useValidateResource,
        useGetDependencyValidation,
        useValidateAllResourcesMultiParented,
        useValidateAllResourcesSingleParented,
        processMutationResponse,
        processGetCollectionResponse,
        processGetResourceResponse,
        processGetResponse,
        batchAddResource,
        batchAddResourceWithParentRelation,
        addOrSetResource,
        addResourceToIndex,
        getSingleFromParent,
        getAllResourcesWithParent,
        changeIndexOfResource,
        getAllResources,
        getParentRelationText,
    };
}
